import React, { useCallback, useEffect, useMemo, useState } from "react";
import Page from "../../components/layout/Helmet";
import Header from "../../components/dashboard/Header";
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Pagination,
  TextField,
} from "@mui/material";
import { t } from "i18next";
import { Search } from "@mui/icons-material";
import { debounce, get, isEmpty } from "lodash";
import { useDataSuccess } from "../../hooks/useDataSuccess";
import { useAuth } from "../../hooks/useAuth";
import Filters from "../../components/dataSuccess/Filters";
import { Dayjs } from "dayjs";
import FileCard from "../../components/dataSuccess/FileCard";
import { hydraTransform } from "../../shared/utils/hydraTransformer";
import { UseFileDownload } from "../../hooks/useFileDownload";
import HorsesLoaderSkeleton from "../../components/horses/HorsesLoaderSkeleton";
import EmptyState from "../../components/global/EmptyState";
import { Icon } from "@iconify/react";
import { deviceType } from "react-device-detect";
import { IPdfRepport } from "../../shared/types/pdfRepports.type";
import { useUserActivityLog } from "../../hooks/useUserActivityLog";

const itemsPerPage = 30;

function DataSuccess() {
  const { auth } = useAuth();
  const { download, isDownloading } = UseFileDownload();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedHorses, setSelectedHorses] = useState<(string | number)[]>([]);
  const [selectedRepport, setSelectedRepport] = useState<(string | number)[]>(
    [],
  );
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    null,
    null,
  ]);

  const [display, setDisplay] = useState<"col" | "row">("col");
  const { track } = useUserActivityLog();

  useEffect(() => {
    track("PDF_ARCHIVE_PAGE_DISPLAYED");
  }, []);

  const options = useMemo(() => {
    let option = [];

    if (search) {
      option.push(`q=${encodeURIComponent(search)}`);
    }
    if (!isEmpty(selectedHorses)) {
      let formattedHOrses = selectedHorses.map(
        (horse) => `${encodeURIComponent("horses[]")}=${horse}`,
      );
      option.push(formattedHOrses.join("&"));
    }
    if (!isEmpty(selectedRepport)) {
      let formattedType = selectedRepport.map(
        (type) => `${encodeURIComponent("types[]")}=${type}`,
      );
      option.push(formattedType.join("&"));
    }

    // Date range
    if (dateRange?.[0]) {
      option.push(
        `data_start_date=${encodeURIComponent(dateRange[0].utc(true).toISOString())}`,
      );
    }
    if (dateRange?.[1]) {
      option.push(
        `data_end_date=${encodeURIComponent(dateRange[1].utc(true).toISOString())}`,
      );
    }

    option.push(`itemsPerPage=${itemsPerPage}`);
    option.push(`page=${page}`);

    return option.join("&");
  }, [search, selectedHorses.length, selectedRepport.length, dateRange, page]);

  const { data, isLoading } = useDataSuccess(auth?.["@id"], options);

  const files = hydraTransform(data?.data || []);

  const totalItems = get(data, "data.hydra:totalItems", 0);

  const count = Math.max(1, Math.ceil(totalItems / itemsPerPage));

  const debouncedSearch = debounce((value: string) => {
    setSearch(value);
  }, 400);

  const resetFilters = useCallback(() => {
    setSelectedHorses([]);
    setSelectedRepport([]);
    setDateRange([null, null]);
    setSearch("");
  }, []);

  const onFileDownload = (
    url: string,
    filename: string,
    type: IPdfRepport["type"],
  ) => {
    fetch("https://eu.i.posthog.com/capture/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        api_key: "phc_o6YLCJ3OVGbl7u2aLXHSBIT3PabFADVYvX6NpbT24BB",
        distinct_id: auth?.["@id"], // un identifiant utilisateur unique
        event: "pdf-download",
        properties: {
          email: auth?.["email"],
          $current_url: url,
          filename: filename,
          $device_type: deviceType,
        },
      }),
    });
    download(url, filename);

    handleTrack(type);
  };

  const handleTrack = (type: IPdfRepport["type"]) => {
    switch (type) {
      case "GALLOP_REVIEW":
        track("ARCHIVE_ADVANCED_COMPARISON_PDF_DOWNLOADED");
        break;
      case "TRAINING":
        track("ARCHIVE_TRAINING_PDF_DOWNLOADED");
        break;
      case "STABLE_REVIEW":
        track("ARCHIVE_STABLE_REVIEW_PDF_DOWNLOADED");
        break;

      default:
        break;
    }
  };

  return (
    <Page withNameApp title={"navbar:dataSuccess"}>
      <Header text="navbar:dataSuccess">
        <TextField
          size="small"
          onChange={(e) => debouncedSearch(e.target.value)}
          placeholder={t("equimetre:search")}
          InputProps={{
            startAdornment: <Search color="disabled"></Search>,
          }}
        ></TextField>
        <Box></Box>
      </Header>
      <Box mb={5} ></Box>
      <Filters
        selectedHorses={selectedHorses}
        onChangeHoses={(values) => setSelectedHorses(values)}
        selectedRepport={selectedRepport}
        onChangeRepportType={(values) => setSelectedRepport(values)}
        dateRange={dateRange}
        onChangeDateRange={(values) => setDateRange(values)}
        resetFilters={resetFilters}
      />
      <Box display={"flex"} justifyContent={"end"} mt={1}>
        <Button
          onClick={() => setDisplay(display === "col" ? "row" : "col")}
          startIcon={
            <Icon
              color="white"
              icon={
                display === "col" ? "grommet-icons:list" : "mynaui:grid-solid"
              }
            />
          }
        >
          {display === "col" ? t("listView") : t("gridView")}
        </Button>
      </Box>
      <Divider sx={{ mb: 2 }}></Divider>
      {isLoading ? (
        <HorsesLoaderSkeleton></HorsesLoaderSkeleton>
      ) : isEmpty(files) ? (
        <EmptyState />
      ) : (
        <>
          <Grid container spacing={1} >
            {files.map((file) => (
              <Grid
                item
                md={display === "col" ? 4 : 12}
                sm={display === "col" ? 6 : 12}
                xs={12}
                key={file["@id"]}
              >
                <FileCard
                  isDownloading={isDownloading}
                  file={file}
                  onDownload={onFileDownload}
                />
              </Grid>
            ))}
          </Grid>
          <Box display={"flex"} justifyContent={"center"} mt={3}>
            <Pagination
              count={count}
              page={page}
              onChange={(_, page) => {
                setPage(page);
              }}
            />
          </Box>
        </>
      )}
    </Page>
  );
}

export default DataSuccess;
